import type { 
  Voucher, 
  CreateVoucherInput, 
  UpdateVoucherInput, 
  VoucherFilters, 
  VoucherListResponse, 
  VoucherStats,
  BulkVoucherAction,
  BulkVoucherResponse,
  VoucherExportOptions
} from "./vouchers.interfaces";
import type { IVouchersService } from "./vouchers.service";

export class VouchersMockService implements IVouchersService {
  private mockData: Voucher[] = [
    {
      id: "1",
      code: "WELCOME2024",
      name: "Welcome Discount 2024",
      description: "Welcome discount for new students",
      status: "Active",
      isActive: true,
      discountType: "percentage",
      discountValue: 20,
      maxUses: 100,
      maxUsesPerUser: 1,
      currentUses: 45,
      validFrom: "2024-01-15T00:00:00Z",
      validUntil: "2024-12-31T23:59:59Z",
      timezone: "UTC",
      packageRestrictions: {
        type: "include",
        packageIds: ["basic-package"]
      },
      createdOn: "2024-01-15T10:00:00Z",
      createdBy: "admin@example.com"
    },
    {
      id: "2",
      code: "SUMMER50",
      name: "Summer Promotion 50%",
      description: "Summer promotion discount",
      status: "Active",
      isActive: true,
      discountType: "percentage",
      discountValue: 50,
      maxUses: 50,
      maxUsesPerUser: 2,
      currentUses: 50,
      validFrom: "2024-06-01T00:00:00Z",
      validUntil: "2024-08-31T23:59:59Z",
      timezone: "UTC",
      packageRestrictions: {
        type: "include",
        packageIds: ["intermediate-package"]
      },
      createdOn: "2024-06-01T09:00:00Z",
      createdBy: "admin@example.com"
    },
    {
      id: "3",
      code: "STUDENT25",
      name: "Student Discount",
      description: "Student discount voucher",
      status: "Inactive",
      isActive: false,
      discountType: "percentage",
      discountValue: 25,
      maxUses: 200,
      maxUsesPerUser: 1,
      currentUses: 150,
      validFrom: "2024-03-01T00:00:00Z",
      validUntil: "2024-05-31T23:59:59Z",
      timezone: "UTC",
      packageRestrictions: {
        type: "include",
        packageIds: ["advanced-package"]
      },
      createdOn: "2024-03-01T14:30:00Z",
      createdBy: "admin@example.com"
    },
    {
      id: "4",
      code: "BLACKFRIDAY",
      name: "Black Friday Special",
      description: "Black Friday special offer",
      status: "Expired",
      isActive: false,
      discountType: "percentage",
      discountValue: 30,
      maxUses: 75,
      maxUsesPerUser: 1,
      currentUses: 75,
      validFrom: "2023-11-20T00:00:00Z",
      validUntil: "2023-11-30T23:59:59Z",
      timezone: "UTC",
      packageRestrictions: {
        type: "include",
        packageIds: ["premium-package"]
      },
      createdOn: "2023-11-20T08:00:00Z",
      createdBy: "admin@example.com"
    },
    {
      id: "5",
      code: "NEWYEAR2024",
      name: "New Year Celebration",
      description: "New Year celebration discount",
      status: "Active",
      isActive: true,
      discountType: "fixedAmount",
      discountValue: 15,
      maxUses: 300,
      maxUsesPerUser: 3,
      currentUses: 120,
      validFrom: "2024-01-01T00:00:00Z",
      validUntil: "2024-02-29T23:59:59Z",
      timezone: "UTC",
      packageRestrictions: {
        type: "include",
        packageIds: ["basic-package", "intermediate-package"]
      },
      createdOn: "2024-01-01T00:00:00Z",
      createdBy: "admin@example.com"
    },
    {
      id: "6",
      code: "SPRING20",
      name: "Spring Season Promotion",
      description: "Spring season promotion",
      status: "Active",
      isActive: true,
      discountType: "percentage",
      discountValue: 20,
      maxUses: 100,
      maxUsesPerUser: 1,
      currentUses: 30,
      validFrom: "2024-03-15T00:00:00Z",
      validUntil: "2024-06-30T23:59:59Z",
      timezone: "UTC",
      packageRestrictions: {
        type: "include",
        packageIds: ["intermediate-package"]
      },
      createdOn: "2024-03-15T12:00:00Z",
      createdBy: "admin@example.com"
    },
    {
      id: "7",
      code: "VIPACCESS",
      name: "VIP Member Exclusive",
      description: "VIP member exclusive discount",
      status: "Inactive",
      isActive: false,
      discountType: "percentage",
      discountValue: 40,
      maxUses: 25,
      maxUsesPerUser: 1,
      currentUses: 25,
      validFrom: "2024-02-01T00:00:00Z",
      validUntil: "2024-04-30T23:59:59Z",
      timezone: "UTC",
      packageRestrictions: {
        type: "include",
        packageIds: ["premium-package"]
      },
      createdOn: "2024-02-01T16:45:00Z",
      createdBy: "admin@example.com"
    },
    {
      id: "8",
      code: "FREESHIP",
      name: "Free Shipping",
      description: "Free shipping for all orders",
      status: "Active",
      isActive: true,
      discountType: "freeShipping",
      discountValue: 0,
      freeShipping: true,
      maxUses: 500,
      maxUsesPerUser: 5,
      currentUses: 89,
      validFrom: "2024-01-01T00:00:00Z",
      validUntil: "2024-12-31T23:59:59Z",
      timezone: "UTC",
      createdOn: "2024-01-01T00:00:00Z",
      createdBy: "admin@example.com"
    }
  ];

  async getVouchers(
    page: number = 1,
    pageSize: number = 10,
    filters?: VoucherFilters
  ): Promise<VoucherListResponse> {
    // Simular delay de rede
    await new Promise(resolve => setTimeout(resolve, 200));

    let filtered = [...this.mockData];

    // Aplicar filtros
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(voucher =>
        voucher.code.toLowerCase().includes(searchLower) ||
        voucher.name.toLowerCase().includes(searchLower) ||
        voucher.description?.toLowerCase().includes(searchLower)
      );
    }

    if (filters?.status) {
      filtered = filtered.filter(voucher => voucher.status === filters.status);
    }

    if (filters?.isActive !== undefined) {
      filtered = filtered.filter(voucher => voucher.isActive === filters.isActive);
    }

    if (filters?.discountType) {
      filtered = filtered.filter(voucher => voucher.discountType === filters.discountType);
    }

    if (filters?.createdAfter) {
      filtered = filtered.filter(voucher => voucher.createdOn >= filters.createdAfter!);
    }

    if (filters?.createdBefore) {
      filtered = filtered.filter(voucher => voucher.createdOn <= filters.createdBefore!);
    }

    if (filters?.validAfter) {
      filtered = filtered.filter(voucher => voucher.validFrom >= filters.validAfter!);
    }

    if (filters?.validBefore) {
      filtered = filtered.filter(voucher => voucher.validUntil <= filters.validBefore!);
    }

    if (filters?.usageRange) {
      filtered = filtered.filter(voucher => 
        voucher.currentUses >= filters.usageRange!.min && 
        voucher.currentUses <= filters.usageRange!.max
      );
    }

    if (filters?.createdBy) {
      filtered = filtered.filter(voucher => voucher.createdBy === filters.createdBy);
    }

    // Aplicar paginação
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = filtered.slice(startIndex, endIndex);

    return {
      vouchers: paginatedData,
      totalCount: filtered.length,
      page,
      pageSize,
      hasMore: endIndex < filtered.length,
    };
  }

  async getVoucherById(id: string): Promise<Voucher> {
    await new Promise(resolve => setTimeout(resolve, 100));

    const voucher = this.mockData.find(v => v.id === id);
    if (!voucher) {
      throw new Error(`Voucher with id ${id} not found`);
    }
    return voucher;
  }

  async createVoucher(data: CreateVoucherInput): Promise<Voucher> {
    await new Promise(resolve => setTimeout(resolve, 300));

    const newVoucher: Voucher = {
      id: String(Date.now()),
      code: data.code,
      name: data.name,
      description: data.description,
      status: data.isActive ? 'Active' : 'Inactive',
      isActive: data.isActive ?? true,
      discountType: data.discountType,
      discountValue: data.discountValue,
      freeShipping: data.freeShipping,
      maxUses: data.maxUses,
      maxUsesPerUser: data.maxUsesPerUser,
      currentUses: 0,
      validFrom: data.validFrom,
      validUntil: data.validUntil,
      timezone: data.timezone,
      packageRestrictions: data.packageRestrictions,
      categoryRestrictions: data.categoryRestrictions,
      productRestrictions: data.productRestrictions,
      createdOn: new Date().toISOString(),
      createdBy: 'admin@example.com',
    };

    this.mockData.push(newVoucher);
    return newVoucher;
  }

  async updateVoucher(id: string, data: UpdateVoucherInput): Promise<Voucher> {
    await new Promise(resolve => setTimeout(resolve, 200));

    const voucher = this.mockData.find(v => v.id === id);
    if (!voucher) {
      throw new Error(`Voucher with id ${id} not found`);
    }

    // Atualizar campos fornecidos
    Object.assign(voucher, data);

    // Sincronizar status e isActive
    if (data.isActive !== undefined) {
      voucher.status = data.isActive ? 'Active' : 'Inactive';
    }
    if (data.status !== undefined) {
      voucher.isActive = data.status === 'Active';
    }

    return voucher;
  }

  async deleteVoucher(id: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 200));

    const index = this.mockData.findIndex(v => v.id === id);
    if (index === -1) {
      throw new Error(`Voucher with id ${id} not found`);
    }

    this.mockData.splice(index, 1);
  }

  async activateVoucher(id: string): Promise<Voucher> {
    await new Promise(resolve => setTimeout(resolve, 150));

    const voucher = this.mockData.find(v => v.id === id);
    if (!voucher) {
      throw new Error(`Voucher with id ${id} not found`);
    }

    voucher.isActive = true;
    voucher.status = 'Active';
    return voucher;
  }

  async deactivateVoucher(id: string): Promise<Voucher> {
    await new Promise(resolve => setTimeout(resolve, 150));

    const voucher = this.mockData.find(v => v.id === id);
    if (!voucher) {
      throw new Error(`Voucher with id ${id} not found`);
    }

    voucher.isActive = false;
    voucher.status = 'Inactive';
    return voucher;
  }

  async extendVoucherValidity(id: string, newValidUntil: string): Promise<Voucher> {
    await new Promise(resolve => setTimeout(resolve, 150));

    const voucher = this.mockData.find(v => v.id === id);
    if (!voucher) {
      throw new Error(`Voucher with id ${id} not found`);
    }

    voucher.validUntil = newValidUntil;
    return voucher;
  }

  async getVoucherStats(id: string): Promise<VoucherStats> {
    await new Promise(resolve => setTimeout(resolve, 100));

    const voucher = this.mockData.find(v => v.id === id);
    if (!voucher) {
      throw new Error(`Voucher with id ${id} not found`);
    }

    return {
      totalUses: voucher.currentUses,
      maxUses: voucher.maxUses || 0,
      usagePercentage: voucher.maxUses ? (voucher.currentUses / voucher.maxUses) * 100 : 0,
      lastUsed: voucher.currentUses > 0 ? new Date().toISOString() : undefined,
      uniqueUsers: Math.floor(voucher.currentUses * 0.8), // Mock data
      averageUsesPerUser: voucher.currentUses > 0 ? voucher.currentUses / Math.floor(voucher.currentUses * 0.8) : 0,
      totalDiscountValue: voucher.currentUses * voucher.discountValue,
      averageDiscountValue: voucher.discountValue,
    };
  }

  // Ações em lote
  async bulkAction(action: BulkVoucherAction): Promise<BulkVoucherResponse> {
    await new Promise(resolve => setTimeout(resolve, 300));

    const success: string[] = [];
    const failed: Array<{ id: string; error: string }> = [];

    for (const id of action.ids) {
      try {
        const voucher = this.mockData.find(v => v.id === id);
        if (!voucher) {
          failed.push({ id, error: 'Voucher not found' });
          continue;
        }

        switch (action.action) {
          case 'activate':
            voucher.isActive = true;
            voucher.status = 'Active';
            break;
          case 'deactivate':
            voucher.isActive = false;
            voucher.status = 'Inactive';
            break;
          case 'delete':
            this.mockData = this.mockData.filter(v => v.id !== id);
            break;
          case 'extend_validity':
            if (action.newValidUntil) {
              voucher.validUntil = action.newValidUntil;
            }
            break;
        }
        success.push(id);
      } catch (error) {
        failed.push({ id, error: error instanceof Error ? error.message : 'Unknown error' });
      }
    }

    return { success, failed };
  }

  async bulkActivateVouchers(ids: string[]): Promise<BulkVoucherResponse> {
    return this.bulkAction({ ids, action: 'activate' });
  }

  async bulkDeactivateVouchers(ids: string[]): Promise<BulkVoucherResponse> {
    return this.bulkAction({ ids, action: 'deactivate' });
  }

  async bulkDeleteVouchers(ids: string[]): Promise<BulkVoucherResponse> {
    return this.bulkAction({ ids, action: 'delete' });
  }

  async bulkExtendValidity(ids: string[], newValidUntil: string): Promise<BulkVoucherResponse> {
    return this.bulkAction({ ids, action: 'extend_validity', newValidUntil });
  }

  // Exportação
  async exportVouchers(
    options: VoucherExportOptions, 
    filters?: VoucherFilters, 
    selectedIds?: string[]
  ): Promise<Blob> {
    await new Promise(resolve => setTimeout(resolve, 500));

    // Simular exportação baseada nos parâmetros
    let dataToExport = this.mockData;
    
    if (selectedIds && selectedIds.length > 0) {
      dataToExport = dataToExport.filter(v => selectedIds.includes(v.id));
    }
    
    // Aplicar filtros se fornecidos
    if (filters) {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        dataToExport = dataToExport.filter(voucher =>
          voucher.code.toLowerCase().includes(searchLower) ||
          voucher.name.toLowerCase().includes(searchLower) ||
          voucher.description?.toLowerCase().includes(searchLower)
        );
      }
      if (filters.status) {
        dataToExport = dataToExport.filter(voucher => voucher.status === filters.status);
      }
      if (filters.isActive !== undefined) {
        dataToExport = dataToExport.filter(voucher => voucher.isActive === filters.isActive);
      }
    }

    // Gerar conteúdo baseado no formato
    let content = '';
    if (options.format === 'csv') {
      content = 'Code,Name,Status,Discount Type,Current Uses,Valid Until\n';
      dataToExport.forEach(v => {
        content += `${v.code},${v.name},${v.status},${v.discountType},${v.currentUses},${v.validUntil}\n`;
      });
    } else {
      // XLSX simulation
      content = 'Code\tName\tStatus\tDiscount Type\tCurrent Uses\tValid Until\n';
      dataToExport.forEach(v => {
        content += `${v.code}\t${v.name}\t${v.status}\t${v.discountType}\t${v.currentUses}\t${v.validUntil}\n`;
      });
    }
    
    const mimeType = options.format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([content], { type: mimeType });
    
    return blob;
  }
}